import { Link } from "react-router-dom";
import { ArrowRight, Award, Target, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { FadeIn, StaggerContainer, StaggerItem } from "@/components/FadeIn";
import { SectionHeading, GoldDivider, GoldDividerLeft } from "@/components/SectionHeading";

const milestones = [
    { year: "2006", title: "Gründung", description: "Gründung der Steuerkanzlei in München" },
    { year: "2012", title: "Expansion", description: "Erweiterung des Teams und der Leistungen" },
    { year: "2018", title: "Digitalisierung", description: "Einführung digitaler Mandantenbetreuung" },
    { year: "2026", title: "Heute", description: "20 Jahre Erfahrung und Expertise" },
];

const principles = [
    {
        icon: Award,
        title: "Exzellenz",
        description: "Wir streben in allem, was wir tun, nach höchster Qualität. Kontinuierliche Weiterbildung und aktuelles Fachwissen sind für uns selbstverständlich.",
    },
    {
        icon: Target,
        title: "Präzision",
        description: "Sorgfalt und Genauigkeit prägen unsere Arbeit. Jedes Mandat wird mit der gebotenen Aufmerksamkeit und Gründlichkeit bearbeitet.",
    },
    {
        icon: Handshake,
        title: "Vertrauen",
        description: "Diskretion und Vertraulichkeit sind die Grundlage unserer Mandantenbeziehungen. Ihr Vertrauen ist unser wertvollstes Gut.",
    },
];

export default function About() {
    return (
        <div className="bg-background">
            {/* Hero Section */}
            <section className="relative pt-32 lg:pt-40 pb-20 lg:pb-24 overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1920&q=80&auto=format&fit=crop"
                        alt="Über uns"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/70 to-background/40" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/30 to-background/60" />
                </div>
                
                <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl">
                        <FadeIn>
                            <span className="font-sans text-xs tracking-wide-premium uppercase text-gold mb-6 block">
                                Über uns
                            </span>
                        </FadeIn>

                        <FadeIn delay={0.1}>
                            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground leading-tight mb-8">
                                Kompetente Steuerberatung mit persönlicher Note
                            </h1>
                        </FadeIn>

                        <FadeIn delay={0.2}>
                            <GoldDividerLeft className="mb-8" />
                        </FadeIn>

                        <FadeIn delay={0.3}>
                            <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
                                Die Steuerkanzlei Ruberto steht seit 2006 für vertrauensvolle 
                                und kompetente Steuerberatung in München. Wir begleiten Unternehmer, 
                                Freiberufler und Privatpersonen bei allen steuerlichen Fragen.
                            </p>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* About Content */}
            <section className="py-20 lg:py-24 bg-charcoal-mid">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    {/* Philosophie Image Banner */}
                    <FadeIn className="mb-16">
                        <div className="relative rounded-lg overflow-hidden">
                            <img
                                src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1400&q=80&auto=format&fit=crop"
                                alt="Professionelles Kanzlei-Ambiente – Steuerberatung Ruberto"
                                className="w-full h-[340px] object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-background/50 to-background/30" />
                            <div className="absolute bottom-8 left-8">
                                <span className="font-sans text-xs tracking-wide-premium uppercase text-gold">Philosophie</span>
                                <h2 className="font-serif text-3xl md:text-4xl text-foreground mt-2">Zeit für Ihre Anliegen</h2>
                            </div>
                        </div>
                    </FadeIn>

                    <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
                        <div>
                            <FadeIn delay={0.1}>
                                <div className="space-y-6 font-sans text-muted-foreground leading-relaxed">
                                    <p>
                                        Wie findet man den richtigen Steuerberater? Diese Frage ist 
                                        etwa so alt wie die Steuergesetzgebung selbst. Und diese ist 
                                        in ihrer ganzen Komplexität selbst für Juristen der wohl am 
                                        schwierigsten zu durchschauende Gesetzestext in unserem Lande.
                                    </p>
                                    <p>
                                        Ein guter Steuerberater sollte sich deshalb vor allem eines 
                                        für Sie nehmen: <span className="text-foreground">Zeit</span>.
                                    </p>
                                    <p>
                                        Ganz gleich, ob es um eine anspruchsvolle Einkommensteuererklärung 
                                        unter Berücksichtigung von Einkünften aus Vermietung und Verpachtung 
                                        geht, um die steuerlichen Auswirkungen einer Erbschaft bzw. Schenkung 
                                        oder um den vermeintlich &bdquo;einfachen&ldquo; Lohnsteuerjahresausgleich.
                                    </p>
                                </div>
                            </FadeIn>
                        </div>

                        <div>
                            <FadeIn delay={0.2}>
                                <div className="space-y-6 font-sans text-muted-foreground leading-relaxed">
                                    <p>
                                        Ob es sich um die steuerlichen Gestaltungsmöglichkeiten rund um 
                                        die Künstlersozialkasse KSK bei Künstlern, Sportlern, Freelancern, 
                                        Journalisten und Influencern handelt oder ob Ihnen die Optimierung 
                                        von Finanzierungs- und Investitionsfragen für Ihr Unternehmen am 
                                        Herzen liegt.
                                    </p>
                                    <p>
                                        Ein gutes Mandat ist stets nur dann ein gutes Mandat, wenn Ihr 
                                        Steuerberater auch den <span className="text-foreground">Menschen</span> bzw. 
                                        das <span className="text-foreground">Unternehmen</span> in all seinen 
                                        Facetten und Zielen versteht, das hinter diesem Mandat steht.
                                    </p>
                                    <p>
                                        An diesem Anspruch dürfen Sie jede Leistung, die wir für Sie 
                                        erbringen, messen.
                                    </p>
                                </div>
                            </FadeIn>

                            <FadeIn delay={0.3}>
                                <div className="mt-10 pt-10 border-t border-border">
                                    <p className="font-serif text-xl text-foreground italic">
                                        &bdquo;Ihr Mario Ruberto, Steuerberater München.&ldquo;
                                    </p>
                                </div>
                            </FadeIn>
                        </div>
                    </div>
                </div>
            </section>

            {/* Principles */}
            <section className="py-24 lg:py-32 bg-background">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <FadeIn>
                        <SectionHeading
                            eyebrow="Grundsätze"
                            title="Unsere Leitprinzipien"
                            alignment="center"
                            className="mb-16 lg:mb-20"
                        />
                    </FadeIn>

                    <StaggerContainer className="grid md:grid-cols-3 gap-8 lg:gap-12" staggerDelay={0.1}>
                        {principles.map((principle) => (
                            <StaggerItem key={principle.title}>
                                <Card className="bg-card border-border p-10 h-full hover:border-gold/30 transition-colors duration-400">
                                    <div className="flex flex-col h-full">
                                        <principle.icon className="h-8 w-8 text-gold mb-8" strokeWidth={1} />
                                        <h3 className="font-serif text-2xl text-foreground mb-4">
                                            {principle.title}
                                        </h3>
                                        <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                                            {principle.description}
                                        </p>
                                    </div>
                                </Card>
                            </StaggerItem>
                        ))}
                    </StaggerContainer>
                </div>
            </section>

            {/* Timeline */}
            <section className="py-24 lg:py-32 bg-charcoal-mid">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <FadeIn>
                        <SectionHeading
                            eyebrow="Geschichte"
                            title="Meilensteine unserer Kanzlei"
                            className="mb-16 lg:mb-20"
                        />
                    </FadeIn>

                    <StaggerContainer className="relative" staggerDelay={0.15}>
                        {/* Timeline line */}
                        <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-px gold-line-vertical opacity-30 transform md:-translate-x-1/2" />

                        <div className="space-y-12">
                            {milestones.map((milestone, index) => (
                                <StaggerItem key={milestone.year}>
                                    <div className={`relative flex flex-col md:flex-row items-start gap-8 ${
                                        index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                                    }`}>
                                        <div className={`md:w-1/2 ${index % 2 === 0 ? "md:text-right md:pr-12" : "md:pl-12"}`}>
                                            <span className="font-serif text-4xl text-gold">
                                                {milestone.year}
                                            </span>
                                            <h3 className="font-serif text-xl text-foreground mt-2 mb-2">
                                                {milestone.title}
                                            </h3>
                                            <p className="font-sans text-sm text-muted-foreground">
                                                {milestone.description}
                                            </p>
                                        </div>
                                        
                                        {/* Dot */}
                                        <div className="absolute left-0 md:left-1/2 top-2 w-3 h-3 bg-gold rounded-full transform md:-translate-x-1/2" />
                                        
                                        <div className="md:w-1/2" />
                                    </div>
                                </StaggerItem>
                            ))}
                        </div>
                    </StaggerContainer>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 lg:py-32 bg-background border-t border-border">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto text-center">
                        <FadeIn>
                            <GoldDivider className="mb-10" />
                        </FadeIn>
                        
                        <FadeIn delay={0.1}>
                            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
                                Lernen Sie uns kennen
                            </h2>
                        </FadeIn>

                        <FadeIn delay={0.2}>
                            <p className="font-sans text-base md:text-lg text-muted-foreground mb-10">
                                Wir freuen uns auf ein persönliches Gespräch mit Ihnen.
                            </p>
                        </FadeIn>

                        <FadeIn delay={0.3}>
                            <Button
                                asChild
                                className="bg-gold text-primary-foreground hover:bg-gold-muted transition-all duration-400 px-10 py-6 font-sans text-sm tracking-premium uppercase"
                            >
                                <Link to="/kontakt">
                                    Kontakt aufnehmen
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                        </FadeIn>
                    </div>
                </div>
            </section>
        </div>
    );
}
